import { Navigate, Route, Routes } from 'react-router-dom';

import { useAuth } from '@/hooks/useAuth';
import { AdminLayout } from '@/layouts/AdminLayout';
import { CompanyLayout } from '@/layouts/CompanyLayout';
import { MarketingLayout } from '@/layouts/MarketingLayout';
import { PublicLayout } from '@/layouts/PublicLayout';
import { StudentLayout } from '@/layouts/StudentLayout';
import { CompanyPage } from '@/pages/CompanyPage';
import { PublicPortfolioPage } from '@/pages/PublicPortfolioPage';
import { AdminAiReportPage } from '@/pages/admin/AiReportPage';
import { AdminAnalyticsPage } from '@/pages/admin/AnalyticsPage';
import { AdminApplicationDetailPage } from '@/pages/admin/ApplicationDetailPage';
import { AdminApplicationsPage } from '@/pages/admin/ApplicationsPage';
import { AdminBillingPage } from '@/pages/admin/BillingPage';
import { AdminPlanDetailPage } from '@/pages/admin/PlanDetailPage';
import { AdminPlansPage } from '@/pages/admin/PlansPage';
import { AdminSubscriptionDetailPage } from '@/pages/admin/SubscriptionDetailPage';
import { AdminSubscriptionsPage } from '@/pages/admin/SubscriptionsPage';
import { AdminCompaniesPage } from '@/pages/admin/CompaniesPage';
import { AdminCompanyDetailPage } from '@/pages/admin/CompanyDetailPage';
import { AdminAnnouncementsPage } from '@/pages/admin/AnnouncementsPage';
import { AdminCouponsPage } from '@/pages/admin/CouponsPage';
import { AdminIntegrationsPage } from '@/pages/admin/IntegrationsPage';
import { AdminMessagesPage } from '@/pages/admin/MessagesPage';
import { AdminAuditPage } from '@/pages/admin/AuditPage';
import { AdminInternshipDetailPage } from '@/pages/admin/InternshipDetailPage';
import { AdminInternshipQueuePage } from '@/pages/admin/InternshipQueuePage';
import { AdminOverviewPage } from '@/pages/admin/OverviewPage';
import { AdminProfilePage } from '@/pages/admin/ProfilePage';
import { AdminReferenceDataPage } from '@/pages/admin/ReferenceDataPage';
import { AdminReportsPage } from '@/pages/admin/ReportsPage';
import { AdminRevenueReportPage } from '@/pages/admin/RevenueReportPage';
import { AdminStudentDetailPage } from '@/pages/admin/StudentDetailPage';
import { AdminRecruiterDetailPage } from '@/pages/admin/RecruiterDetailPage';
import { AdminRecruitersPage } from '@/pages/admin/RecruitersPage';
import { AdminRecruitmentPage } from '@/pages/admin/RecruitmentPage';
import { AdminStudentsPage } from '@/pages/admin/StudentsPage';
import { AdminUsersPage } from '@/pages/admin/UsersPage';
import { AdminStudentVerificationsPage } from '@/pages/admin/StudentVerificationsPage';
import { AdminDocumentVerificationsPage } from '@/pages/admin/DocumentVerificationsPage';
import { AdminVerificationReportPage } from '@/pages/admin/VerificationReportPage';
import { AdminVerificationsPage } from '@/pages/admin/VerificationsPage';
import { InternshipDetailPage } from '@/pages/InternshipDetailPage';
import { InternshipsPage } from '@/pages/InternshipsPage';
import { AboutPage } from '@/pages/AboutPage';
import { BlogPage } from '@/pages/BlogPage';
import { LandingPage } from '@/pages/LandingPage';
import { NotFoundPage } from '@/pages/NotFoundPage';
import { NotificationsPage } from '@/pages/NotificationsPage';
import { StudentsPage } from '@/pages/StudentsPage';
import { CompaniesPage } from '@/pages/CompaniesPage';
import { LoginPage } from '@/pages/auth/LoginPage';
import { ForgotPasswordPage, ResetPasswordPage } from '@/pages/auth/PasswordPages';
import { RegisterCompanyPage } from '@/pages/auth/RegisterCompanyPage';
import { RegisterStudentPage } from '@/pages/auth/RegisterStudentPage';
import { VerifyEmailPage } from '@/pages/auth/VerifyEmailPage';
import { CompanyAnalyticsPage } from '@/pages/company/AnalyticsPage';
import { CompanyBillingPage } from '@/pages/company/BillingPage';
import { CompanyBrandingPage } from '@/pages/company/BrandingPage';
import { CompanyIntegrationsPage } from '@/pages/company/IntegrationsPage';
import { MessagesPage } from '@/pages/MessagesPage';
import { CompanyApplicationDetailPage } from '@/pages/company/ApplicationDetailPage';
import { CompanyApplicationsPage } from '@/pages/company/ApplicationsPage';
import { CandidateDetailPage } from '@/pages/company/CandidateDetailPage';
import { CandidateSearchPage } from '@/pages/company/CandidateSearchPage';
import { CompanyDashboardPage } from '@/pages/company/DashboardPage';
import { CompanyInternshipDetailPage } from '@/pages/company/InternshipDetailPage';
import { CompanyInternshipFormPage } from '@/pages/company/InternshipFormPage';
import { CompanyInternshipPerformancePage } from '@/pages/company/InternshipPerformancePage';
import { CompanyInternshipsPage } from '@/pages/company/InternshipsPage';
import { CompanyInterviewsPage } from '@/pages/company/InterviewsPage';
import { CompanyOffersPage } from '@/pages/company/OffersPage';
import { CompanyProfilePage } from '@/pages/company/ProfilePage';
import { CompanySourcingPage } from '@/pages/company/SourcingPage';
import { TalentPoolPage } from '@/pages/company/TalentPoolPage';
import { CompanyTeamPage } from '@/pages/company/TeamPage';
import { CompanyRolesPage } from '@/pages/company/RolesPage';
import { CompanyAccessPage } from '@/pages/company/AccessPage';
import { CandidatesSection } from '@/layouts/CandidatesSection';
import { CompanyProfileSection } from '@/layouts/CompanyProfileSection';
import { CompanyRecommendationsPage } from '@/pages/company/RecommendationsPage';
import { CompanyVerificationGuidePage } from '@/pages/company/VerificationGuidePage';
import { CompanyVerificationPage } from '@/pages/company/VerificationPage';
import { StudentApplicationDetailPage } from '@/pages/student/ApplicationDetailPage';
import { StudentCareerPage } from '@/pages/student/CareerPage';
import { StudentCvPage } from '@/pages/student/CvPage';
import { StudentCvFeedbackPage } from '@/pages/student/CvFeedbackPage';
import { StudentPlanPage } from '@/pages/student/PlanPage';
import { StudentCvReviewPage } from '@/pages/student/CvReviewPage';
import { StudentDashboardPage } from '@/pages/student/DashboardPage';
import { StudentEducationPage } from '@/pages/student/EducationPage';
import { StudentExperiencePage } from '@/pages/student/ExperiencePage';
import { StudentInsightsPage } from '@/pages/student/InsightsPage';
import { StudentInterviewsPage } from '@/pages/student/InterviewsPage';
import { StudentOffersPage } from '@/pages/student/OffersPage';
import { StudentProfilePage } from '@/pages/student/ProfilePage';
import { MyInternshipsPage } from '@/pages/student/MyInternshipsPage';
import { StudentRecommendationsPage } from '@/pages/student/RecommendationsPage';
import { StudentInvitePage } from '@/pages/student/InvitePage';
import { StudentSkillsPage } from '@/pages/student/SkillsPage';
import { PERMISSIONS } from '@/types/roles';

import { ProtectedRoute } from './ProtectedRoute';
import { RequirePermission } from './RequirePermission';
import { PublicRoute } from './PublicRoute';
import { paths } from './paths';

/**
 * Application route table.
 *
 * Public marketing and discovery sit under PublicLayout; the signed-in student,
 * company and admin areas sit behind ProtectedRoute, which also enforces the role.
 */
export function AppRoutes() {
  return (
    <Routes>
      {/* The landing page carries the dark Wonder design and a header that
          overlays its hero, so it gets its own shell. Everything else public
          is still on the light chrome until it is redesigned. */}
      <Route element={<MarketingLayout />}>
        <Route path={paths.home} element={<LandingPage />} />
        <Route path={paths.blog} element={<BlogPage />} />
        <Route path={paths.about} element={<AboutPage />} />
        <Route path={paths.forStudents} element={<StudentsPage />} />
        <Route path={paths.forCompanies} element={<CompaniesPage />} />
      </Route>

      <Route element={<PublicLayout />}>
        <Route path={paths.internshipDetail()} element={<InternshipDetailPage />} />
        <Route path={paths.companyPublic()} element={<CompanyPage />} />
        <Route path={paths.publicPortfolio()} element={<PublicPortfolioPage />} />

        {/* Signed-in users are bounced away from the auth screens. */}
        <Route element={<PublicRoute />}>
          <Route path={paths.login} element={<LoginPage />} />
          <Route path={paths.admin.login} element={<LoginPage portal="admin" />} />
          <Route path={paths.registerStudent} element={<RegisterStudentPage />} />
          <Route path={paths.registerCompany} element={<RegisterCompanyPage />} />
          <Route path={paths.forgotPassword} element={<ForgotPasswordPage />} />
          <Route path={paths.resetPassword} element={<ResetPasswordPage />} />
        </Route>

        {/* Verification is reachable while signed in but unverified. */}
        <Route path={paths.verifyEmail} element={<VerifyEmailPage />} />

        <Route path={paths.notFound} element={<NotFoundPage />} />
      </Route>

      {/* One notifications page for every role, inside that role's own shell.
          Declared once: the same path under three role guards resolved to
          whichever came first, and sent everyone else home. */}
      <Route element={<ProtectedRoute allowedRoles={['SUPER_ADMIN', 'COMPANY', 'STUDENT']} />}>
        <Route element={<RoleLayout />}>
          <Route path={paths.notifications} element={<NotificationsPage />} />
          {/* Browsing internships needs an account, and happens inside the
              signed-in shell with its sidebar. A visitor is sent to log in
              and brought back here. */}
          <Route path={paths.internships} element={<InternshipsPage />} />
        </Route>
      </Route>

      <Route element={<ProtectedRoute allowedRoles={['SUPER_ADMIN']} />}>
        <Route element={<AdminLayout />}>
          <Route path={paths.admin.root} element={<Navigate to={paths.admin.overview} replace />} />
          <Route path={paths.admin.overview} element={<AdminOverviewPage />} />
          <Route path={paths.admin.profile} element={<AdminProfilePage />} />
          <Route path={paths.admin.verifications} element={<AdminVerificationsPage />} />
          <Route
            path={paths.admin.studentVerifications}
            element={<AdminStudentVerificationsPage />}
          />
          <Route
            path={paths.admin.documentVerifications}
            element={<AdminDocumentVerificationsPage />}
          />
          <Route path={paths.admin.internships} element={<AdminInternshipQueuePage />} />
          <Route path={paths.admin.internshipDetail()} element={<AdminInternshipDetailPage />} />
          <Route path={paths.admin.companies} element={<AdminCompaniesPage />} />
          <Route path={paths.admin.companyDetail()} element={<AdminCompanyDetailPage />} />
          <Route path={paths.admin.students} element={<AdminStudentsPage />} />
          <Route path={paths.admin.studentDetail()} element={<AdminStudentDetailPage />} />
          <Route path={paths.admin.recruiters} element={<AdminRecruitersPage />} />
          <Route path={paths.admin.recruiterDetail()} element={<AdminRecruiterDetailPage />} />
          <Route path={paths.admin.recruitment} element={<AdminRecruitmentPage />} />
          <Route path={paths.admin.users} element={<AdminUsersPage />} />
          <Route path={paths.admin.reports} element={<AdminReportsPage />} />
          <Route path={paths.admin.applications} element={<AdminApplicationsPage />} />
          <Route path={paths.admin.applicationDetail()} element={<AdminApplicationDetailPage />} />
          <Route path={paths.admin.audit} element={<AdminAuditPage />} />
          <Route path={paths.admin.analytics} element={<AdminAnalyticsPage />} />
          <Route path={paths.admin.revenueReport} element={<AdminRevenueReportPage />} />
          <Route path={paths.admin.verificationReport} element={<AdminVerificationReportPage />} />
          <Route path={paths.admin.aiReport} element={<AdminAiReportPage />} />
          <Route
            path="/admin/reference-data"
            element={<Navigate to={paths.admin.universities} replace />}
          />
          <Route path={paths.admin.referenceData()} element={<AdminReferenceDataPage />} />
          <Route path={paths.admin.billing} element={<AdminBillingPage />} />
          <Route path={paths.admin.plans} element={<AdminPlansPage />} />
          <Route path={paths.admin.coupons} element={<AdminCouponsPage />} />
          <Route path={paths.admin.announcements} element={<AdminAnnouncementsPage />} />
          <Route path={paths.admin.planDetail()} element={<AdminPlanDetailPage />} />
          <Route path={paths.admin.subscriptions} element={<AdminSubscriptionsPage />} />
          <Route
            path={paths.admin.subscriptionDetail()}
            element={<AdminSubscriptionDetailPage />}
          />
          <Route path={paths.admin.integrations} element={<AdminIntegrationsPage />} />
          <Route path={paths.admin.messages} element={<AdminMessagesPage />} />
        </Route>
      </Route>

      <Route element={<ProtectedRoute allowedRoles={['COMPANY']} />}>
        <Route element={<CompanyLayout />}>
          <Route
            path={paths.company.root}
            element={<Navigate to={paths.company.dashboard} replace />}
          />
          <Route path={paths.company.dashboard} element={<CompanyDashboardPage />} />
          {/* "Company profile" in the sidebar: three tabs, each its own URL. */}
          <Route element={<CompanyProfileSection />}>
            <Route element={<RequirePermission permission={PERMISSIONS.COMPANY_PROFILE_UPDATE} />}>
              <Route path={paths.company.profile} element={<CompanyProfilePage />} />
            </Route>
            <Route element={<RequirePermission permission={PERMISSIONS.COMPANY_BRANDING_UPDATE} />}>
              <Route path={paths.company.branding} element={<CompanyBrandingPage />} />
            </Route>
            <Route path={paths.company.access} element={<CompanyAccessPage />} />
          </Route>
          {/* Each group needs the permission its API reads with, so a member
              who arrives by URL gets an explanation rather than 403s. */}
          <Route element={<RequirePermission permission={PERMISSIONS.INTERNSHIP_READ} />}>
            <Route path={paths.company.internships} element={<CompanyInternshipsPage />} />
            <Route
              path={paths.company.internshipDetail()}
              element={<CompanyInternshipDetailPage />}
            />
            <Route
              path={paths.company.internshipPerformance()}
              element={<CompanyInternshipPerformancePage />}
            />
          </Route>
          <Route element={<RequirePermission permission={PERMISSIONS.INTERNSHIP_CREATE} />}>
            <Route path={paths.company.internshipNew} element={<CompanyInternshipFormPage />} />
          </Route>
          <Route
            element={
              <RequirePermission
                permission={[PERMISSIONS.INTERNSHIP_READ, PERMISSIONS.INTERNSHIP_UPDATE]}
              />
            }
          >
            <Route path={paths.company.internshipEdit()} element={<CompanyInternshipFormPage />} />
          </Route>
          <Route
            element={
              <RequirePermission
                permission={[PERMISSIONS.INTERNSHIP_READ, PERMISSIONS.CANDIDATE_READ]}
              />
            }
          >
            <Route path={paths.company.internshipSourcing()} element={<CompanySourcingPage />} />
          </Route>
          <Route element={<RequirePermission permission={PERMISSIONS.APPLICATION_READ} />}>
            <Route path={paths.company.applications} element={<CompanyApplicationsPage />} />
            <Route
              path={paths.company.applicationDetail()}
              element={<CompanyApplicationDetailPage />}
            />
          </Route>
          <Route element={<RequirePermission permission={PERMISSIONS.CANDIDATE_READ} />}>
            {/* "Find candidates" in the sidebar: search and saved candidates as tabs. */}
            <Route element={<CandidatesSection />}>
              <Route path={paths.company.candidates} element={<CandidateSearchPage />} />
              <Route path={paths.company.talentPool} element={<TalentPoolPage />} />
            </Route>
            <Route path={paths.company.candidateDetail()} element={<CandidateDetailPage />} />
          </Route>
          <Route element={<RequirePermission permission={PERMISSIONS.INTERVIEW_CREATE} />}>
            <Route path={paths.company.interviews} element={<CompanyInterviewsPage />} />
            {/* The recruitment dashboard's calendar: the same page, calendar only. */}
            <Route
              path={paths.company.interviewCalendar}
              element={<CompanyInterviewsPage calendarOnly />}
            />
          </Route>
          <Route element={<RequirePermission permission={PERMISSIONS.OFFER_CREATE} />}>
            <Route path={paths.company.offers} element={<CompanyOffersPage />} />
          </Route>
          {/* Its links check their own permissions; any member may see the list. */}
          <Route path={paths.company.recommendations} element={<CompanyRecommendationsPage />} />
          <Route element={<RequirePermission permission={PERMISSIONS.ANALYTICS_READ} />}>
            <Route path={paths.company.analytics} element={<CompanyAnalyticsPage />} />
          </Route>
          <Route element={<RequirePermission permission={PERMISSIONS.SUBSCRIPTION_READ} />}>
            <Route path={paths.company.billing} element={<CompanyBillingPage />} />
          </Route>
          <Route element={<RequirePermission permission={PERMISSIONS.ATS_READ} />}>
            <Route path={paths.company.integrations} element={<CompanyIntegrationsPage />} />
          </Route>
          <Route element={<RequirePermission permission={PERMISSIONS.MESSAGE_READ} />}>
            {/* The same screen on both shells: the API resolves which side the
                caller is on, so there is nothing here to duplicate. */}
            <Route path={paths.company.messages} element={<MessagesPage />} />
          </Route>
          <Route path={paths.company.team} element={<CompanyTeamPage />} />
          <Route path={paths.company.roles} element={<CompanyRolesPage />} />
          <Route path={paths.company.verification} element={<CompanyVerificationPage />} />
          <Route element={<RequirePermission permission={PERMISSIONS.COMPANY_PROFILE_UPDATE} />}>
            <Route
              path={paths.company.verificationGuide}
              element={<CompanyVerificationGuidePage />}
            />
          </Route>
        </Route>
      </Route>

      <Route element={<ProtectedRoute allowedRoles={['STUDENT']} />}>
        <Route element={<StudentLayout />}>
          <Route
            path={paths.student.root}
            element={<Navigate to={paths.student.dashboard} replace />}
          />
          <Route path={paths.student.dashboard} element={<StudentDashboardPage />} />
          <Route path={paths.student.profile} element={<StudentProfilePage />} />
          <Route path={paths.student.education} element={<StudentEducationPage />} />
          <Route path={paths.student.experience} element={<StudentExperiencePage />} />
          <Route path={paths.student.skills} element={<StudentSkillsPage />} />
          <Route path={paths.student.career} element={<StudentCareerPage />} />
          <Route path={paths.student.cv} element={<StudentCvPage />} />
          <Route path={paths.student.cvReview()} element={<StudentCvReviewPage />} />
          <Route path={paths.student.cvFeedback()} element={<StudentCvFeedbackPage />} />
          {/* Applied and Saved: one page, one URL per tab. For you is its own
              Recommendations page and keeps its old URL. */}
          <Route
            path={paths.student.internships}
            element={<Navigate to={paths.student.applications} replace />}
          />
          <Route path={paths.student.saved} element={<MyInternshipsPage />} />
          {/* <Route path={paths.student.recommendations} element={<MyInternshipsPage />} /> */}
          <Route path={paths.student.recommendations} element={<StudentRecommendationsPage />} />
          <Route path={paths.student.applications} element={<MyInternshipsPage />} />
          <Route
            path={paths.student.legacy.saved}
            element={<Navigate to={paths.student.saved} replace />}
          />
          <Route
            path={paths.student.legacy.recommendations}
            element={<Navigate to={paths.student.recommendations} replace />}
          />
          <Route
            path={paths.student.legacy.applications}
            element={<Navigate to={paths.student.applications} replace />}
          />
          <Route
            path={paths.student.applicationDetail()}
            element={<StudentApplicationDetailPage />}
          />
          <Route path={paths.student.interviews} element={<StudentInterviewsPage />} />
          <Route path={paths.student.offers} element={<StudentOffersPage />} />
          <Route path={paths.student.messages} element={<MessagesPage />} />
          <Route path={paths.student.insights} element={<StudentInsightsPage />} />
          {/* Account is a section of the profile page now; old links still land on it. */}
          <Route
            path={paths.student.account}
            element={<Navigate to={`${paths.student.profile}#account`} replace />}
          />
          <Route path={paths.student.invite} element={<StudentInvitePage />} />
          <Route path={paths.student.plan} element={<StudentPlanPage />} />
        </Route>
      </Route>
    </Routes>
  );
}

/** The signed-in user's own shell, for pages every role shares. */
function RoleLayout() {
  const { user } = useAuth();
  if (user?.role === 'SUPER_ADMIN') return <AdminLayout />;
  if (user?.role === 'COMPANY') return <CompanyLayout />;
  return <StudentLayout />;
}
